import React, { useEffect, useState } from 'react';
import {
    Container, Typography, Box, Button, Divider, Stack,
    Card, CardContent, CardMedia, Chip, CircularProgress
} from '@mui/material';
import { CalendarToday, Person, Article, Image as ImageIcon, VideoLibrary } from '@mui/icons-material';
import NavbarMain from '../components/NavbarMain';
import blogApi from '../services/blogApi';
import Footer from "../components/Footer";
import SEO from '../components/SEO'; // ✅ ДОДАДЕНО

const Blog = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    const isAdmin = localStorage.getItem('user') &&
        JSON.parse(localStorage.getItem('user'))?.role === 'ADMIN';

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            const data = await blogApi.getAllPosts();
            const sorted = (data || []).sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
            setPosts(sorted);
        } catch (err) {
            console.error('Error loading posts', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePublish = async (id) => {
        await blogApi.togglePublish(id);
        fetchPosts();
    };

    const handleDelete = async (id) => {
        if (window.confirm('Дали си сигурен дека сакаш да ја избришеш статијата?')) {
            await blogApi.deletePost(id);
            fetchPosts();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('mk-MK', {
            year: 'numeric', month: 'long', day: 'numeric'
        });
    };

    const getMediaUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('https://') || url.startsWith('http://')) return url;
        return `${process.env.REACT_APP_API_URL}${url}`;
    };

    return (
        <>
            {/* ✅ SEO */}
            <SEO
                title="Блог - BodyLab by Anamarija | Фитнес Совети и Исхрана"
                description="Читај стручни статии за фитнес, исхрана, диети и здрав живот. Бесплатни совети и информации од BodyLab by Anamarija."
                url="/blog"
            />

            <NavbarMain />
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #ffeef8 0%, #e0f7ff 100%)',
                pt: 12, pb: 10
            }}>
                <Container maxWidth="md">

                    {/* ===== HEADER ===== */}
                    <Box textAlign="center" mb={8}>
                        <Box sx={{
                            width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 3,
                            background: 'linear-gradient(135deg, #ff7eb9, #a5d8ff)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 8px 32px rgba(255,126,185,0.3)'
                        }}>
                            <Article sx={{ fontSize: 38, color: 'white' }} />
                        </Box>

                        {/* ✅ H1 */}
                        <Typography variant="h1" sx={{
                            fontWeight: 900,
                            background: 'linear-gradient(45deg, #ff7eb9, #a5d8ff)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            mb: 1.5, fontSize: { xs: '2.2rem', md: '3rem' }
                        }}>
                            Блог
                        </Typography>

                        {/* ✅ H2 */}
                        <Typography variant="h2" sx={{
                            color: '#888', fontWeight: 400,
                            fontSize: { xs: '1rem', md: '1.25rem' }
                        }}>
                            Совети, статии и информации за фитнес и здравје
                        </Typography>
                        <Box sx={{
                            width: 60, height: 4, borderRadius: 2,
                            background: 'linear-gradient(90deg, #ff7eb9, #a5d8ff)',
                            mx: 'auto', mt: 2
                        }} />
                    </Box>

                    {/* ===== CONTENT ===== */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: '#ff7eb9' }} />
                        </Box>
                    ) : posts.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 10 }}>
                            <Typography variant="h5" sx={{ color: '#aaa', mb: 1 }}>
                                📭 Нема објавени статии
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#bbb' }}>
                                Наскоро ќе бидат достапни нови содржини!
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={4}>
                            {posts.map((post, index) => (
                                <Card key={post.id} sx={{
                                    borderRadius: '20px',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid rgba(255,126,185,0.15)',
                                    boxShadow: '0 4px 24px rgba(255,126,185,0.08)',
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 40px rgba(255,126,185,0.18)',
                                        borderColor: 'rgba(255,126,185,0.35)'
                                    }
                                }}>
                                    {/* ✅ SLIKA */}
                                    {post.imageUrl && (
                                        <CardMedia
                                            component="img"
                                            height={280}
                                            image={getMediaUrl(post.imageUrl)}
                                            alt={post.title}
                                            sx={{ objectFit: 'cover' }}
                                        />
                                    )}

                                    {/* ✅ VIDEO */}
                                    {post.videoUrl && !post.imageUrl && (
                                        <Box sx={{
                                            width: '100%', backgroundColor: '#000',
                                            borderRadius: '20px 20px 0 0',
                                            overflow: 'hidden', position: 'relative'
                                        }}>
                                            <video controls preload="metadata"
                                                   style={{ width: '100%', maxHeight: '380px', display: 'block' }}>
                                                <source src={getMediaUrl(post.videoUrl)} type="video/mp4" />
                                                <source src={getMediaUrl(post.videoUrl)} type="video/webm" />
                                            </video>
                                            <Chip
                                                icon={<VideoLibrary sx={{ fontSize: '14px !important' }} />}
                                                label="Видео" size="small"
                                                sx={{
                                                    position: 'absolute', top: 12, left: 12,
                                                    bgcolor: 'rgba(171,71,188,0.85)',
                                                    color: 'white', fontWeight: 'bold',
                                                    backdropFilter: 'blur(4px)'
                                                }}
                                            />
                                        </Box>
                                    )}

                                    {!post.imageUrl && !post.videoUrl && (
                                        <Box sx={{
                                            height: 5,
                                            background: index % 2 === 0
                                                ? 'linear-gradient(90deg, #ff7eb9, #AB47BC)'
                                                : 'linear-gradient(90deg, #a5d8ff, #ff7eb9)'
                                        }} />
                                    )}

                                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                            <Chip label="Блог" size="small" sx={{
                                                bgcolor: 'rgba(255,126,185,0.1)', color: '#ff7eb9',
                                                fontWeight: 'bold', fontSize: '0.7rem',
                                                border: '1px solid rgba(255,126,185,0.3)'
                                            }} />
                                            {post.imageUrl && (
                                                <Chip icon={<ImageIcon sx={{ fontSize: '12px !important' }} />}
                                                      label="Слика" size="small"
                                                      sx={{ bgcolor: 'rgba(255,126,185,0.08)', color: '#ff7eb9', fontSize: '0.65rem' }} />
                                            )}
                                            {post.videoUrl && (
                                                <Chip icon={<VideoLibrary sx={{ fontSize: '12px !important' }} />}
                                                      label="Видео" size="small"
                                                      sx={{ bgcolor: 'rgba(171,71,188,0.1)', color: '#AB47BC', fontSize: '0.65rem' }} />
                                            )}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CalendarToday sx={{ fontSize: 14, color: '#aaa' }} />
                                                <Typography variant="caption" sx={{ color: '#aaa' }}>
                                                    {formatDate(post.createdAt)}
                                                </Typography>
                                            </Box>
                                            {post.author && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Person sx={{ fontSize: 14, color: '#aaa' }} />
                                                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                                                        {post.author}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {post.published === false && (
                                                <Chip label="НЕОБЈАВЕНО" size="small"
                                                      sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 'bold', fontSize: '0.65rem' }} />
                                            )}
                                        </Box>

                                        {/* ✅ H3 за секоја статија */}
                                        <Typography variant="h3" sx={{
                                            fontWeight: 800, mb: 2, color: '#1a1a1a',
                                            lineHeight: 1.3, fontSize: { xs: '1.4rem', md: '1.9rem' }
                                        }}>
                                            {post.title}
                                        </Typography>

                                        <Divider sx={{ mb: 2.5, borderColor: 'rgba(255,126,185,0.15)' }} />

                                        <Box sx={{
                                            fontSize: '1.05rem', lineHeight: 1.85, color: '#444',
                                            '& img': { maxWidth: '100%', borderRadius: '10px' },
                                            '& p': { margin: '0 0 12px 0' },
                                            '& h1, & h2, & h3': { color: '#222' },
                                            '& a': { color: '#ff7eb9' }
                                        }}
                                             dangerouslySetInnerHTML={{ __html: post.content }}
                                        />

                                        {isAdmin && (
                                            <>
                                                <Divider sx={{ my: 3, borderColor: 'rgba(255,126,185,0.15)' }} />
                                                <Stack direction="row" spacing={2}>
                                                    <Button variant="contained" size="small"
                                                            color={post.published ? 'warning' : 'success'}
                                                            onClick={() => handleTogglePublish(post.id)}
                                                            sx={{ borderRadius: '8px', fontWeight: 'bold' }}>
                                                        {post.published ? 'Тргни' : 'Објави'}
                                                    </Button>
                                                    <Button variant="outlined" size="small" color="error"
                                                            onClick={() => handleDelete(post.id)}
                                                            sx={{ borderRadius: '8px', fontWeight: 'bold' }}>
                                                        Избриши
                                                    </Button>
                                                </Stack>
                                            </>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    )}
                </Container>
            </Box>
            <Footer />
        </>
    );
};

export default Blog;